import { getRegion } from '@utils/misc';
import { TDataLayer } from '@typescript/index';
import { IItemCart } from '@reaffiliates/typescript';
import { ICartData, CartProductPricesType } from '@features/cart/typescript';
import { getCurrencyByRegion, toISO4217CurrencyCode } from '@utils/formatPrice';
import { safeParsePrice, safeExtractPriceFromDOM } from '@utils/safePriceParser';
import priceParser from '@utils/priceParser';
interface ICartStoreProduct {
  productId: string;
  prices: CartProductPricesType;
}
const GoogleTagManagerAddToCart = (products: ICartData[]) => {
  const region = getRegion();
  const currency = toISO4217CurrencyCode(getCurrencyByRegion(region));
  const items: Array<IItemCart> = [];
  let cartStoreProducts: Map<string, ICartStoreProduct> | null = null;
  try {
    if (typeof window !== 'undefined' && window.__CART_STORE__) {
      cartStoreProducts = window.__CART_STORE__.products;
    }
  } catch (error) {
    console.error('GoogleTagManagerAddToCart: Error accessing CartStore:', error);
  }
  const getFallbackPrice = (): number => {
    const selectors = ['.vpw-cart__price-value', '.st-main-menu-price-value', '.price-container > span', '.fc_front_price'];
    for (const selector of selectors) {
      const price = safeExtractPriceFromDOM(selector, currency);
      if (price > 0) {
        return price;
      }
    }
    return 0;
  };
  const fallbackPrice = getFallbackPrice();
  let totalPrice = 0;
  products.forEach((product: ICartData) => {
    let productPrice = 0;
    if (product.price !== undefined && product.price !== null) {
      productPrice = safeParsePrice(product.price, currency);
    }
    if (productPrice === 0 && cartStoreProducts) {
      const cartProduct = Array.from(cartStoreProducts.values()).find(p => p.productId === product.productId.toString());
      if (cartProduct?.prices?.total?.current?.gross?.amount) {
        productPrice = priceParser(cartProduct.prices.total.current.gross.amount.toString());
      }
    }
    if (productPrice === 0 && fallbackPrice > 0) {
      productPrice = fallbackPrice;
    }
    if (productPrice === 0) {
      console.warn(`GoogleTagManagerAddToCart: Could not determine price for product ${product.productId} (${product.name}). Skipping or using 0.`);
    }
    const configuration = typeof product.configuration === 'string' ? JSON.parse(product.configuration) : product.configuration;
    items.push({
      item_name: product.name,
      item_id: product.productId,
      quantity: product.quantity,
      item_category: configuration?.type || '',
      price: productPrice
    });
    totalPrice += productPrice * product.quantity;
  });
  if (totalPrice === 0 && fallbackPrice > 0) {
    totalPrice = fallbackPrice * products.reduce((sum, p) => sum + p.quantity, 0);
  }
  if (isNaN(totalPrice) || !isFinite(totalPrice) || totalPrice < 0) {
    console.warn(`GoogleTagManagerAddToCart: Invalid total price ${totalPrice}. Cannot send DataLayer event.`);
    return;
  }
  const validItems = items.filter(item => {
    if (isNaN(item.price) || !isFinite(item.price) || item.price < 0) {
      console.warn(`GoogleTagManagerAddToCart: Removing item with invalid price: ${item.item_name} (${item.item_id})`);
      return false;
    }
    return true;
  });
  if (validItems.length === 0) {
    console.warn('GoogleTagManagerAddToCart: No valid items to send. Skipping DataLayer event.');
    return;
  }
  const tagManagerArgs: TDataLayer = {
    event: 'add_to_cart',
    ecommerce: {
      value: totalPrice,
      currency,
      items: validItems
    }
  };
  if (window && window.dataLayer) {
    window.dataLayer.push({
      ecommerce: null
    });
    window.dataLayer.push(tagManagerArgs);
  }
};
export default GoogleTagManagerAddToCart;